/**
 * @module lib/audience/data-collection
 */
import tgamConfig from "config/tgam";
import ee from "lib/eventemitter";
import { getUserSophiId } from "lib/identity/api";
import crow from "utils/crow";
import logger from "utils/logger";
import isNil from "lodash/isNil";
import { handleClickEvent, trackGTMEvent } from "./gtm-manager";
import { mark, measure } from "lib/performance/user-timing";
import { isDarwinDisabled } from "lib/darwin/tests";
import { getProp } from "utils/utils";

const debugTag = "[DATA-COLLECTION]";

/**
 * @method addGalleryInteractionHandlers
 */
function addGalleryInteractionHandlers() {
  if (tgamConfig.datalayer.article.pagesubtype !== "gallery") {
    return;
  }
  let scrolledPhotos = 0;
  let totalPhotosViewed = 0;
  let galleryPhotos = null;
  let galleryPhotoTracker = null;
  let galleryOpened = false;
  const hook = ".js-c-article-body img";
  const LOG = "[GALLERY]";

  function trackPhotos(photos) {
    const lastPhoto = photos.length;
    const halfWayPhoto = parseInt(photos.length / 2, 10);

    function getMilestone() {
      totalPhotosViewed++;
      if (totalPhotosViewed === halfWayPhoto) {
        return "Half-way photo gallery view";
      }
      if (totalPhotosViewed === (lastPhoto)) {
        return "Completed photo gallery";
      }
      return "Photo gallery view";
    }

    return {
      getMilestone
    };
  }

  galleryPhotos = document.querySelectorAll(hook);
  galleryPhotoTracker = trackPhotos(galleryPhotos);

  function sendGalleryGtmEvent(galleryData) {
    if (isNil(galleryData)) {
      return;
    }
    let galleryEventObj = {
      feature: "photo gallery",
      type: "gallery",
      position: !isNil(galleryData._custom.position) ? galleryData._custom.position : undefined,
      totalImages: !isNil(galleryPhotos) ? galleryPhotos.length : undefined,
      viewMode: !isNil(galleryData._custom.viewMode) ? galleryData._custom.viewMode : undefined,
      contentId: getProp(tgamConfig, "datalayer.article.contentId", undefined),
      milestone: galleryData.milestone
    };
    ee.trigger("analytics.trackGTMEvent", [galleryEventObj, "engagementEvent"]);
  }

  function scrolledPhotoEvent() {
    scrolledPhotos++;
    if (scrolledPhotos > totalPhotosViewed) {
      const milestone = galleryPhotoTracker.getMilestone();
      let galleryData = {
        _custom: {
          position: totalPhotosViewed,
          viewMode: "scroll mode"
        },
        milestone: milestone
      };
      sendGalleryGtmEvent(galleryData);
    }
  };

  window.addEventListener("load", () => {
    let i = 0;
    const allImgs = document.querySelectorAll(".js-c-article-body .js-lazyimage--loaded").length;
    const imgDiff = allImgs - totalPhotosViewed;
    for (i; i < imgDiff; i++) {
      scrolledPhotoEvent();
    }
  });

  document.addEventListener("lazybeforeunveil", (e) => {
    if (!e.target.closest(".js-c-article-body")) {
      return;
    }
    scrolledPhotoEvent();
  });

  ee.addListener("tgam-photo-slider:selected", (slider) => {
    let slideType = slider.getSlideType();
    const photoNotViewedInScrollMode = (slider.getIndex() + 1 - slider.getNumberOfAdSlidesBeforeCurrent()) > totalPhotosViewed;
    if (!galleryOpened && slideType !== "ad" && photoNotViewedInScrollMode) {
      //gallery opens for the first time, but photo hasn't been viewed in scroll mode, so no engagement event was dispatched for this photo, so we need to send an engagement event for this photo in gallery mode with image position
      logger.info(`${LOG} Gallery opened for the first time, but photo hasn't been viewed in scroll mode`);
      galleryOpened = true;
      const milestone = galleryPhotoTracker.getMilestone();
      let galleryData = {
        _custom: {
          position: totalPhotosViewed,
          viewMode: "gallery mode"
        },
        milestone: milestone
      };
      sendGalleryGtmEvent(galleryData);
    } else if (!galleryOpened && slideType !== "ad") {
      //gallery opens for the first time, but photo has already been viewed in scroll mode, so just dispatch an event that the gallery was opened for the first time but without the image position as it already was reported in scroll mode
      galleryOpened = true;
      logger.info(`${LOG} Gallery opened for the first time, but photo has already been viewed in scroll mode`);
      let galleryData = {
        _custom: {
          viewMode: "gallery mode"
        }
      };
      sendGalleryGtmEvent(galleryData);
    }

    //images are clicked in gallery mode and if they have not been seen before in scroll mode - dispatch an engagement event with image position
    if (photoNotViewedInScrollMode && slideType !== "ad") {
      logger.info(`${LOG} Image viewed for the first time in gallery but never in scroll mode`);
      const milestone = galleryPhotoTracker.getMilestone();
      let galleryData = {
        _custom: {
          position: totalPhotosViewed,
          viewMode: "gallery mode"
        },
        milestone: milestone
      };
      sendGalleryGtmEvent(galleryData);
    }
  });
}

/**
 * @method bootstrapClickTracking
 * @description handles all clicks of the document element
 */
function bootstrapClickTracking() {
  logger.info(`${debugTag} bootstrapClickTracking()`);
  document.addEventListener("click", (e) => {
    handleClickEvent(e);
  });
}

/**
 * @method collectAllTheThings
 * @description collects all the things
 */
function collectAllTheThings() {
  logger.info(`${debugTag} collectAllTheThings()`);
  try {
    tgamConfig.datalayer.identity.visid = getUserSophiId();
    bootstrapClickTracking();
    if (tgamConfig.meta.isStoryPage || tgamConfig.meta.isVideoPage) {
      addGalleryInteractionHandlers();
    }
  } catch (e) {
    logger.error(`${debugTag} data-collection exception: ${e}`);
  }
}

/**
 * @method addDarwinTestsToDatalayer
 * @description Determine which Darwin variants ran.
 */
export function addDarwinTestsToDatalayer() {
  const disabled = isDarwinDisabled();
  logger.info(`${debugTag} addDarwinTestsToDatalayer()`, !disabled);
  if (disabled) {
    tgamConfig.datalayer.darwinTests = null;
    return;
  }
  window._dw.push(["global", "ready", function onReady(api) {
    try {
      const darwinArray = api.get(".get_history").toTrackVarWithSegments();
      tgamConfig.datalayer.darwinTests = darwinArray.length ? darwinArray.join(",") : null;
      logger.info(`${debugTag} darwin tests:`, darwinArray);
    } catch (e) {
      crow.error(e, {
        feature: "darwin : addDarwinTestsToDatalayer"
      });
    }
  }]);
}

/**
 * @method delayGTM
 * @description destructs arguments from event emitter and passes it through to `trackGTMEvent`
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Destructuring_assignment} for further information.
 */
function delayGTM() {
  // array destructuring tested and working in IE11 and all other browsers
  const [data, event, eventCallback] = arguments;

  // surrounding `trackGTMEvent` with a setTimeout will move it to the end of the callstack, allowing other functions to run first
  setTimeout(() => {
    trackGTMEvent({ data, event, eventCallback });
  }, 0);
}

/**
 * @method init
 * @description initializes data collection
 */
export default function init() {
  mark("data_collection_started");
  logger.groupCollapsed(`${debugTag}`);
  if (tgamConfig.env.analyticsDisabled) {
    logger.info(`${debugTag} -- ANALYTICS DISABLED --`);
  } else {
    ee.on("analytics.trackGTMEvent", delayGTM);
    addDarwinTestsToDatalayer();
    collectAllTheThings();
  }
  logger.groupEnd(`${debugTag}`);
  mark("data_collection_done");
  measure("data_collection");
}
